import type { ButtonTone } from "../Button";
import type { ConfirmDialogIconColors } from "../../Dialog/Internal/ConfirmDialog";

// Standard button: primary/secondary × tone matrix — filled-brand primary,
// white tinted-border secondary in the tone color.
export type ButtonRoleKey = "primary" | "secondary";

export const standardStyles: Record<
  ButtonRoleKey,
  Record<ButtonTone, string>
> = {
  primary: {
    neutral: "border border-transparent bg-brand text-white hover:bg-brand/85",
    caution:
      "border border-transparent bg-warning text-white hover:bg-warning/85",
    danger: "border border-transparent bg-red text-white hover:bg-red/85",
  },
  secondary: {
    // Neutral has no semantic tone, so in dark mode use white text/icons — the
    // light blue is too low-contrast on a dark surface (esp. thin icon strokes).
    // caution/danger keep their colour since it carries meaning and reads on dark.
    // The border needs the same dark-mode escape as the text above it:
    // `very-light-blue` is #bec4ed on light but #1e2247 on dark, a hair off the
    // card colour, so the outline all but disappeared and the button stopped
    // reading as a button. `border-strong` is the neutral that's built to sit
    // visibly on a dark surface.
    neutral:
      "border border-very-light-blue dark:border-border-strong bg-card text-brand-text dark:text-white hover:bg-brand-subtle",
    caution: "border border-warning/40 bg-card text-warning hover:bg-warning/5",
    danger: "border border-red/40 bg-card text-red hover:bg-red/5",
  },
};

export const linkStyles: Record<ButtonTone, string> = {
  neutral: "text-brand-text hover:text-brand-text-hover",
  caution: "text-warning hover:text-warning/80",
  danger: "text-red hover:text-red/80",
};

export const sizeStyles: Record<"default" | "compact" | "large", string> = {
  default: "px-3 py-1.5",
  compact: "px-3 py-1",
  // Prominent standalone CTAs (e.g. Select Quote) — matches Cargo's "large".
  large: "px-5 py-2.5",
};

export const disabledStyles = "opacity-40 pointer-events-none";

// Icon action: a compact, borderless, muted icon button. Used by `Button` when
// `iconAction` is set or it's inside a Dialog header-actions slot — the look
// formerly owned by the standalone DialogActionButton. Works anywhere (dialog
// headers, panel bodies), coexisting with bordered ControlBar buttons.
// `color` tints the icon for emphasis (e.g. text-blue-text); when set, the hover only
// changes the background so the tint is preserved. `hasGap` adds the gap between
// the icon and a trailing count (`badge`) or a visible `label`.
export function iconActionClasses(
  color: string | undefined,
  disabled: boolean,
  hasGap: boolean,
): string {
  const colorClass = color ?? "text-content-faint";
  const interaction = disabled
    ? "cursor-not-allowed opacity-40"
    : color
      ? "hover:bg-muted"
      : "hover:bg-muted hover:text-foreground";
  return `inline-flex items-center${hasGap ? " gap-1.5" : ""} rounded-md px-2 py-1 ${colorClass} transition-colors ${interaction}`;
}

// Confirm dialog icon colors by tone
export const toneIconColors: Record<ButtonTone, ConfirmDialogIconColors> = {
  neutral: {
    bg: "bg-foreground/10",
    border: "border-foreground/30",
    fg: "text-foreground",
  },
  caution: {
    bg: "bg-orange/20",
    border: "border-orange/40",
    fg: "text-orange",
  },
  danger: { bg: "bg-red/15", border: "border-red/40", fg: "text-red" },
};

// Menu item text color by tone
export const menuToneClass: Record<ButtonTone, string> = {
  neutral: "",
  caution: "text-orange",
  danger: "text-red",
};

// A rounded row with a solid brand selection on highlight (Radix sets
// data-highlighted for both hover and keyboard). `group` lets the leading icon
// flip to white on the highlight; the menu content adds `px-1` so the rounded
// highlight insets from the edges.
export const menuItemClasses =
  "group flex w-full cursor-pointer items-center gap-2 rounded-md py-1.5 pr-6 pl-3 text-left text-[13px] font-medium whitespace-nowrap text-foreground transition-colors outline-none data-[highlighted]:bg-brand data-[highlighted]:text-white";

/**
 * A submenu trigger: the same row with a tighter right gutter, since the caret
 * fills the space a plain row leaves empty.
 *
 * The padding is swapped, not appended. Two `pr-*` classes on one element are
 * resolved by stylesheet order, which Tailwind emits ascending — so appending
 * `pr-3` to a string that already says `pr-6` lost every time, and the caret
 * sat a full gutter short of the edge. Both values are still literal in this
 * file, so the JIT generates them.
 */
export const menuSubTriggerClasses = menuItemClasses.replace("pr-6", "pr-3");
